export const CAMPAIGN_ROW_SUMMARY_BROWSER_DEADLINE_MS = 18_000;

function canonicalCampaignIds(campaignIds) {
  return [...new Set((Array.isArray(campaignIds) ? campaignIds : [])
    .filter((value) => typeof value === 'string' && value.trim() === value && value.length > 0))]
    .sort((left, right) => left.localeCompare(right));
}

export function campaignRowSummaryReadScopeKey(companyId, campaignIds, datePreset) {
  const ids = canonicalCampaignIds(campaignIds);
  if (!companyId || !datePreset || ids.length === 0) return '';
  return JSON.stringify([companyId, ids, datePreset]);
}

function readFailure(code, message, name = 'AbortError') {
  const error = new Error(message);
  error.name = name;
  error.code = code;
  return error;
}

function timeoutFailure() {
  return readFailure(
    'campaign_row_summaries_timeout',
    'Campaign row summaries exceeded their browser deadline.',
    'TimeoutError',
  );
}

function abortedFailure() {
  return readFailure(
    'campaign_row_summaries_aborted',
    'Campaign row summary read was superseded.',
  );
}

export function isCurrentCampaignRowSummaryRead({
  mounted,
  currentScope,
  expectedScope,
  currentRequestId,
  requestId,
}) {
  return mounted === true
    && Boolean(expectedScope)
    && currentScope === expectedScope
    && currentRequestId === requestId;
}

export function createCampaignRowSummaryReadCoordinator({
  deadlineMs = CAMPAIGN_ROW_SUMMARY_BROWSER_DEADLINE_MS,
  schedule = setTimeout,
  cancelSchedule = clearTimeout,
} = {}) {
  if (!Number.isFinite(deadlineMs) || deadlineMs <= 0) {
    throw new TypeError('Campaign row summary deadline must be positive.');
  }
  if (typeof schedule !== 'function' || typeof cancelSchedule !== 'function') {
    throw new TypeError('Campaign row summary scheduler is unavailable.');
  }

  const entries = new Map();
  let nextRequestId = 0;

  function abortEntry(entry, error = abortedFailure()) {
    if (!entry || entries.get(entry.scope) !== entry) return false;
    entries.delete(entry.scope);
    if (entry.deadlineTimer) cancelSchedule(entry.deadlineTimer);
    if (entry.abortTimer) cancelSchedule(entry.abortTimer);
    if (!entry.controller.signal.aborted) entry.controller.abort(error);
    entry.rejectCancellation(error);
    return true;
  }

  function read(scope, execute, { replace = false } = {}) {
    if (!scope || typeof scope !== 'string') {
      throw new TypeError('Campaign row summary scope is required.');
    }
    if (typeof execute !== 'function') {
      throw new TypeError('Campaign row summary executor is required.');
    }

    const existing = entries.get(scope);
    if (existing && !replace) {
      if (existing.abortTimer) {
        cancelSchedule(existing.abortTimer);
        existing.abortTimer = null;
      }
      return existing.claim;
    }
    if (existing) abortEntry(existing);

    const controller = new AbortController();
    let rejectCancellation;
    const cancellation = new Promise((_, reject) => {
      rejectCancellation = reject;
    });
    let request;
    try {
      request = Promise.resolve(execute(controller.signal));
    } catch (error) {
      request = Promise.reject(error);
    }
    const entry = {
      scope,
      controller,
      deadlineTimer: null,
      abortTimer: null,
      rejectCancellation,
      claim: null,
    };
    const requestId = ++nextRequestId;
    const promise = Promise.race([request, cancellation]).finally(() => {
      if (entries.get(scope) === entry) entries.delete(scope);
      if (entry.deadlineTimer) cancelSchedule(entry.deadlineTimer);
      if (entry.abortTimer) cancelSchedule(entry.abortTimer);
      entry.deadlineTimer = null;
      entry.abortTimer = null;
    });
    entry.claim = Object.freeze({ requestId, promise });
    entries.set(scope, entry);
    entry.deadlineTimer = schedule(() => abortEntry(entry, timeoutFailure()), deadlineMs);
    return entry.claim;
  }

  return Object.freeze({
    read,
    abort(scope) {
      return abortEntry(entries.get(scope));
    },
    scheduleAbort(scope) {
      const entry = entries.get(scope);
      if (!entry || entry.abortTimer) return false;
      entry.abortTimer = schedule(() => {
        entry.abortTimer = null;
        abortEntry(entry);
      }, 0);
      return true;
    },
    has(scope) {
      return entries.has(scope);
    },
  });
}

export const campaignRowSummaryReadCoordinator = createCampaignRowSummaryReadCoordinator();
